import { HttpError, useStores } from '@littlebird/sdk';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { joinSegmentText } from '../meetings-transcript.utils';
import { meetingStore } from '../meetings.state';

import type { useProcessedTranscripts } from '../meetings-transcript-display.hooks';

type Selection = {
  meetingId: string;
  transcript: string;
  chunks: ReturnType<typeof useProcessedTranscripts>;
  keys: Set<number>;
  anchor: number | null;
  confirmingDelete: boolean;
};

type SelectionModifiers = {
  shiftKey: boolean;
  metaKey: boolean;
  ctrlKey: boolean;
};

export const useTranscriptSelection = ({
  meetingId,
  transcript,
  transcripts,
  canDelete,
  isRecording = false,
  onSelectionCleared,
}: {
  meetingId: string;
  transcript: string;
  transcripts: ReturnType<typeof useProcessedTranscripts>;
  canDelete: boolean;
  isRecording?: boolean;
  onSelectionCleared?: () => void;
}) => {
  const { meetings } = useStores();
  const [selection, setSelection] = useState<Selection>();
  const [isDeleting, setIsDeleting] = useState(false);
  const pointerSelection = useRef<Selection | undefined>(undefined);
  const confirmTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const selectableChunks = transcripts.filter(
    ({ segments }, index) =>
      !(isRecording && index === transcripts.length - 1) &&
      segments.every((segment) => segment.isFinal)
  );
  const chunksByKey = new Map(
    selectableChunks.map((chunk) => [chunk.sourceLineIndex, chunk])
  );
  const selectionChanged = selection?.chunks.some((previous) => {
    const chunk = chunksByKey.get(previous.sourceLineIndex);
    return (
      !chunk ||
      chunk.speaker !== previous.speaker ||
      joinSegmentText(chunk.segments) !== joinSegmentText(previous.segments)
    );
  });
  let current =
    selection?.meetingId === meetingId &&
    transcript.startsWith(selection.transcript) &&
    !selectionChanged
      ? selection
      : undefined;
  if (current && current.transcript !== transcript) {
    current = { ...current, transcript, confirmingDelete: false };
  }
  if (selection !== current) setSelection(current);
  const selectableKeys = new Set(chunksByKey.keys());
  const selectedChunks = selectableChunks.filter(({ sourceLineIndex }) =>
    current?.keys.has(sourceLineIndex)
  );
  const selectedKeys = new Set(
    selectedChunks.map(({ sourceLineIndex }) => sourceLineIndex)
  );

  useEffect(
    () => () => {
      clearTimeout(confirmTimer.current);
      pointerSelection.current = undefined;
    },
    [meetingId, transcript]
  );

  const clearSelection = () => {
    clearTimeout(confirmTimer.current);
    setSelection(undefined);
    onSelectionCleared?.();
  };

  const rememberPointerSelection = () => {
    pointerSelection.current = selection;
  };

  const restorePointerSelection = () => {
    clearTimeout(confirmTimer.current);
    const previous = pointerSelection.current;
    setSelection(
      previous ? { ...previous, confirmingDelete: false } : undefined
    );
  };

  const selectChunk = (key: number, modifiers: SelectionModifiers) => {
    if (isDeleting || !selectableKeys.has(key)) return;
    clearTimeout(confirmTimer.current);
    let keys = new Set([key]);
    let anchor = key;
    if (
      modifiers.shiftKey &&
      current?.anchor != null &&
      selectableKeys.has(current.anchor)
    ) {
      const start = selectableChunks.findIndex(
        (chunk) => chunk.sourceLineIndex === current.anchor
      );
      const end = selectableChunks.findIndex(
        (chunk) => chunk.sourceLineIndex === key
      );
      keys = new Set(
        selectableChunks
          .slice(Math.min(start, end), Math.max(start, end) + 1)
          .map((chunk) => chunk.sourceLineIndex)
      );
      anchor = current.anchor;
    } else if (modifiers.metaKey || modifiers.ctrlKey) {
      keys = new Set(selectedKeys);
      if (keys.has(key)) keys.delete(key);
      else keys.add(key);
    } else if (selectedKeys.size === 1 && selectedKeys.has(key)) {
      keys.clear();
    }
    setSelection({
      meetingId,
      transcript,
      chunks: selectableChunks.filter(
        (chunk) =>
          keys.has(chunk.sourceLineIndex) || chunk.sourceLineIndex === anchor
      ),
      keys,
      anchor,
      confirmingDelete: false,
    });
  };

  const copySelection = async () => {
    const text = selectedChunks
      .map(
        ({ speaker, segments }) =>
          (speaker ? `[${speaker}]: ` : '') + joinSegmentText(segments)
      )
      .join('\n');
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Transcript copied');
    } catch (error) {
      console.error('Failed to copy transcript chunks', error);
      toast.error("Couldn't copy the selected chunks");
    }
  };

  const deleteSelection = async () => {
    if (!canDelete || isDeleting || !current || !selectedChunks.length) return;
    if (!current.confirmingDelete) {
      setSelection({ ...current, confirmingDelete: true });
      confirmTimer.current = setTimeout(() => {
        setSelection((previous) =>
          previous ? { ...previous, confirmingDelete: false } : previous
        );
      }, 3000);
      return;
    }
    await deleteChunks(
      selectedChunks.map(({ sourceLineIndex }) => sourceLineIndex)
    );
  };

  const deleteChunks = async (chunkIndices: number[]) => {
    clearTimeout(confirmTimer.current);
    setIsDeleting(true);
    try {
      const updated = await meetings.deleteTranscriptChunks(
        meetingId,
        transcript,
        chunkIndices
      );
      meetingStore.setCommittedTranscript(
        meetingId,
        updated.personifiedTranscript
      );
      clearSelection();
      toast.success('Transcript chunks deleted', {
        description:
          updated.searchRefreshStatus === 'pending'
            ? 'Search is updating automatically.'
            : undefined,
      });
    } catch (error) {
      console.error('Failed to delete transcript chunks', error);
      const latestTranscript = meetings.get(meetingId)?.personifiedTranscript;
      if (latestTranscript !== undefined) {
        meetingStore.setCommittedTranscript(meetingId, latestTranscript ?? '');
        if (latestTranscript !== transcript) clearSelection();
      }
      toast.error(
        error instanceof HttpError &&
          error.detail?.error_code === 'transcript_deletion_unavailable'
          ? 'Transcript deletion is temporarily unavailable. Try again later.'
          : error instanceof HttpError && error.status === 409
            ? 'The transcript changed or is still recording. Review the refreshed transcript.'
            : "Couldn't confirm deletion. The latest available transcript is shown."
      );
      setSelection((previous) =>
        previous ? { ...previous, confirmingDelete: false } : previous
      );
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    selectableKeys,
    selectedKeys,
    selectedCount: selectedChunks.length,
    confirmingDelete: current?.confirmingDelete ?? false,
    isDeleting,
    selectChunk,
    rememberPointerSelection,
    restorePointerSelection,
    clearSelection,
    copySelection,
    deleteSelection,
  };
};
